import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'

interface TermsDialogProps {
  isOpen: boolean
  onClose: () => void
}

const TermsDialog: React.FC<TermsDialogProps> = ({ isOpen, onClose }) => {

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-900">Terms and Conditions</h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="prose prose-gray max-w-none text-gray-900">
                <h3 className="text-lg font-semibold mb-4 text-gray-900">Acceptance of Terms</h3>
                <p className="mb-4 text-gray-700">
                  By accessing and using our services, you accept and agree to be bound by the terms 
                  and provision of this agreement. If you do not agree to abide by the above, please 
                  do not use this service.
                </p>

                <h3 className="text-lg font-semibold mb-4 text-gray-900">Use License</h3>
                <p className="mb-4 text-gray-700">
                  Permission is granted to temporarily download one copy of our services for personal, 
                  non-commercial transitory viewing only. This is the grant of a license, not a transfer 
                  of title, and under this license you may not modify or copy the materials.
                </p>

                <h3 className="text-lg font-semibold mb-4 text-gray-900">Service Availability</h3>
                <p className="mb-4 text-gray-700">
                  We strive to provide continuous service availability, but we do not guarantee that our 
                  services will be available at all times. We reserve the right to modify, suspend, or 
                  discontinue any part of our services without notice.
                </p>

                <h3 className="text-lg font-semibold mb-4 text-gray-900">User Responsibilities</h3>
                <p className="mb-4 text-gray-700">
                  You are responsible for maintaining the confidentiality of your account and password. 
                  You agree to accept responsibility for all activities that occur under your account 
                  or password. You must not use our services for any unlawful purpose or any purpose 
                  prohibited under this clause.
                </p>

                <h3 className="text-lg font-semibold mb-4 text-gray-900">Intellectual Property</h3>
                <p className="mb-4 text-gray-700">
                  All content, trademarks, service marks, trade names, logos, and other intellectual 
                  property rights in and to our services are owned by Logicloud Mexico or our licensors. 
                  You may not use, reproduce, or distribute any content without our prior written consent.
                </p>

                <h3 className="text-lg font-semibold mb-4 text-gray-900">Limitation of Liability</h3>
                <p className="mb-4 text-gray-700">
                  In no event shall Logicloud Mexico, nor its directors, employees, partners, agents, 
                  suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, 
                  or punitive damages, including without limitation, loss of profits, data, use, goodwill, 
                  or other intangible losses, resulting from your use of our services.
                </p>

                <h3 className="text-lg font-semibold mb-4 text-gray-900">Indemnification</h3>
                <p className="mb-4 text-gray-700">
                  You agree to defend, indemnify, and hold harmless Logicloud Mexico and its licensee 
                  and licensors, and their employees, contractors, agents, officers and directors, from 
                  and against any and all claims, damages, obligations, losses, liabilities, costs or 
                  debt, and expenses (including but not limited to attorney's fees).
                </p>

                <h3 className="text-lg font-semibold mb-4 text-gray-900">Termination</h3>
                <p className="mb-4 text-gray-700">
                  We may terminate or suspend your account and bar access to our services immediately, 
                  without prior notice or liability, under our sole discretion, for any reason whatsoever 
                  and without limitation, including but not limited to a breach of the Terms.
                </p>

                <h3 className="text-lg font-semibold mb-4 text-gray-900">Governing Law</h3>
                <p className="mb-4 text-gray-700">
                  These Terms shall be interpreted and governed by the laws of Mexico, without regard to 
                  its conflict of law provisions. Our failure to enforce any right or provision of these 
                  Terms will not be considered a waiver of those rights.
                </p>

                <h3 className="text-lg font-semibold mb-4 text-gray-900">Changes to Terms</h3>
                <p className="mb-4 text-gray-700">
                  We reserve the right, at our sole discretion, to modify or replace these Terms at any 
                  time. If a revision is material, we will provide at least 30 days notice prior to any 
                  new terms taking effect.
                </p>

                <h3 className="text-lg font-semibold mb-4 text-gray-900">Contact Information</h3>
                <p className="mb-4 text-gray-700">
                  If you have any questions about these Terms and Conditions, please contact us at:
                  <br />
                  Email: info@logicloud.mx
                  <br />
                  Address: Mexico City, Mexico
                </p>

                <p className="text-sm text-gray-600 mt-6">
                  Last Updated: {new Date().toLocaleDateString()}
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default TermsDialog
